/* eslint-disable complexity */
import * as Yup from 'yup';
import { CreateCalendarFormFieldObject } from '@params/redux/calendar_form_builder';
import CustomInput from '../../components/reusable/input';
import { FormikProps } from 'formik';
import {
    emailOptional,
    emailRequired,
    numberOptional,
    numberRequired,
    phoneNumberOptional,
    phoneNumberRequired,
    stringOptional,
    stringRequired
} from '../../schema';

type FormValues = {
    [key: string]: unknown;
};

// Generate initial values dynamically
export const generateInitialValues = (fields: CreateCalendarFormFieldObject[]): FormValues => {
    const initialValues: FormValues = {};

    fields.forEach((field) => {
        if (!field.id) return;

        switch (field.type) {
            case 'checkbox':
                initialValues[field.id] = [];
                break;

            case 'rating':
            case 'slider':
                initialValues[field.id] = 0;
                break;

            case 'file':
            case 'image':
                initialValues[field.id] = null;
                break;

            case 'heading':
            case 'description':
            case 'submit':
                // These don't require form state
                break;

            default:
                initialValues[field.id] = '';
        }
    });

    return initialValues;
};

// Generate validation schema based on field configs
export const generateValidationSchema = (fields: CreateCalendarFormFieldObject[]) => {
    const shape: Record<string, Yup.AnySchema> = {};
    fields.forEach((field) => {
        if (!field.id) return;
        const isRequired = field.required;
        switch (field.type) {
            case 'text':
            case 'multiline':
                shape[field.id] = isRequired ? stringRequired : stringOptional;
                break;

            case 'email':
                shape[field.id] = isRequired ? emailRequired : emailOptional;
                break;

            case 'phone':
                shape[field.id] = isRequired ? phoneNumberRequired : phoneNumberOptional;
                break;

            case 'rating':
            case 'slider':
            case 'decimal':
            case 'percentage':
            case 'currency':
            case 'number':
                shape[field.id] = isRequired ? numberRequired : numberOptional;
                break;

            case 'date':
                shape[field.id] = Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/u, 'Invalid date format');
                break;

            default:
                break;
        }
    });
    // console.log('Validation Schema:', shape);
    return Yup.object().shape(shape);
};

// Render dynamic fields using Formik props
export const renderDynamicFields = (fields: CreateCalendarFormFieldObject[], formikProps: FormikProps<FormValues>) => {
    return fields.map((field) => {
        const fieldName = field.id as string;

        switch (field.type) {
            case 'multiline':
                return (
                    <CustomInput
                        key={fieldName}
                        fullWidth
                        type="textarea"
                        rows={4}
                        label={field.label}
                        placeholder={field.placeholder}
                        name={fieldName}
                        value={formikProps.values[fieldName] as string}
                        onChange={formikProps.handleChange(fieldName)}
                        onBlur={formikProps.handleBlur(fieldName)}
                        disabled={formikProps.isSubmitting}
                        error={formikProps.touched[fieldName] && Boolean(formikProps.errors[fieldName])}
                    />
                );
            case 'phone':
                return (
                    <CustomInput
                        key={fieldName}
                        type="phone"
                        fullWidth
                        value={formikProps.values[fieldName] as string}
                        onChangeForPhone={formikProps.handleChange(fieldName)}
                        onBlur={formikProps.handleBlur(fieldName)}
                        error={formikProps.touched[fieldName] && Boolean(formikProps.errors[fieldName])}
                        label={field.label}
                        placeholder={field.placeholder}
                        variant="outlined"
                        disabled={formikProps.isSubmitting}
                        mandatoryFields={true}
                    />
                );
            case 'date':
                return (
                    <CustomInput
                        key={fieldName}
                        name={fieldName}
                        label={field.label}
                        placeholder={field.placeholder}
                        value={formikProps.values[fieldName] as Date}
                        onBlur={formikProps.handleBlur(fieldName)}
                        fullWidth
                        variant="outlined"
                        mandatoryFields={field.required}
                        error={formikProps.touched[fieldName] && Boolean(formikProps.errors[fieldName])}
                        disabled={formikProps.isSubmitting}
                        type="date"
                        onChange={formikProps.handleChange(fieldName)}
                    />
                );
            case 'heading':
                switch (field.heading_size) {
                    case 'h1':
                        return (<h1 style={{ color: field.labelColor, fontFamily: field.fontFamily }}>{field.label}</h1>);
                    case 'h2':
                        return (<h2 style={{ color: field.labelColor, fontFamily: field.fontFamily }}>{field.label}</h2>);
                    case 'h3':
                        return (<h3 style={{ color: field.labelColor, fontFamily: field.fontFamily }}>{field.label}</h3>);
                    case 'h4':
                        return (<h4 style={{ color: field.labelColor, fontFamily: field.fontFamily }}>{field.label}</h4>);
                    case 'h5':
                        return (<h5 style={{ color: field.labelColor, fontFamily: field.fontFamily }}>{field.label}</h5>);
                    case 'h6':
                        return (<h6 style={{ color: field.labelColor, fontFamily: field.fontFamily }}>{field.label}</h6>);
                    default:
                        return null;
                }
                return null;
            case 'description':
                return (
                    <p style={{ color: field.labelColor, fontFamily: field.fontFamily }}>{field.label}</p>
                );
            case 'decimal':
            case 'number':
            case 'email':
            case 'text':
                return (
                    <CustomInput
                        key={fieldName}
                        fullWidth
                        type={field.type === 'decimal' || field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
                        label={field.label}
                        placeholder={field.placeholder}
                        name={fieldName}
                        value={formikProps.values[fieldName] as string}
                        onChange={formikProps.handleChange(fieldName)}
                        onBlur={formikProps.handleBlur(fieldName)}
                        disabled={formikProps.isSubmitting}
                        error={formikProps.touched[fieldName] && Boolean(formikProps.errors[fieldName])}
                    />
                );
            default:
                return null;
        }
    });
};
